import { Component, Inject, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { FormlyFieldConfig, FormlyModule } from '@ngx-formly/core';
import { FormlyMaterialModule } from '@ngx-formly/material';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Subject, takeUntil } from 'rxjs';
import dayjs from 'dayjs';
import {
	KapchargeLocalTransaction,
	PaymentProcessingService,
	TransactionUpdatePayload,
} from '@lmc-app/core/services/payment-processing.service';
import { KapchargeTransactionStatus } from '@lmc-app/shared/enum/kapcharge-transaction-status.enum';

export interface TransactionEditDialogData {
	transaction: KapchargeLocalTransaction;
}

/** The saved row as the server returns it, or undefined when the dialog was cancelled. */
export type TransactionEditDialogResult = KapchargeLocalTransaction | undefined;

interface TransactionEditModel {
	/** Dollars — the row stores cents. */
	amount: number | null;
	institutionCode: string;
	transitNumber: string;
	accountNumber: string;
}

/** Strips the currency mask's separators; NaN for anything unparseable. */
function toCents(value: unknown): number {
	return Math.round(Number(String(value ?? '').replace(/,/g, '')) * 100);
}

/**
 * Edits a transaction that has not been sent to KapCharge yet. Deliberately a plain
 * updater: it persists whichever fields changed and does not validate them against NLS.
 * The submit cron is the gate — it matches the NLS payment for any row without an
 * nlsTransactionId and holds back what it cannot match — so adding a field here means adding
 * one entry to `fields` and nothing else.
 *
 * Saving an Invalid row re-queues it, since the cron only ever picks up Queued rows. Changing
 * the amount clears the row's nlsTransactionId server-side, so the cron re-matches it.
 */
@Component({
	selector: 'app-transaction-edit-dialog',
	standalone: true,
	imports: [
		CommonModule,
		ReactiveFormsModule,
		FormlyModule,
		FormlyMaterialModule,
		MatDialogModule,
		MatButtonModule,
		MatIconModule,
		MatProgressSpinnerModule,
	],
	templateUrl: './transaction-edit-dialog.component.html',
	changeDetection: ChangeDetectionStrategy.Eager,
	styleUrls: ['./transaction-edit-dialog.component.scss'],
})
export class TransactionEditDialogComponent implements OnDestroy {
	readonly form = new FormGroup({});
	readonly model: TransactionEditModel;

	/** Part of the payment-history lookup key, so it is shown but never edited. */
	readonly dueDate: string;

	/**
	 * Institution is 3 digits and transit 5 — the same split applied to an NLS aba_number
	 * (0 + institution + transit) everywhere else in this feature. Lengths match the model
	 * columns: STRING(10) / STRING(10) / STRING(20).
	 */
	readonly fields: FormlyFieldConfig[] = [
		{
			fieldGroupClassName: 'row',
			fieldGroup: [
				{
					className: 'col-12',
					key: 'amount',
					type: 'currency',
					props: {
						label: 'Amount',
						required: true,
					},
					validators: {
						positive: {
							expression: (c: { value: unknown }) => toCents(c.value) > 0,
							message: () => 'Amount must be greater than zero',
						},
					},
				},
				{
					className: 'col-4',
					key: 'institutionCode',
					type: 'input',
					props: {
						label: 'Institution Code',
						required: true,
						maxLength: 3,
						pattern: /^\d{3}$/,
						appearance: 'outline',
						attributes: { autocomplete: 'off', inputmode: 'numeric' },
					},
					validation: {
						messages: { pattern: 'Institution code must be exactly 3 digits' },
					},
				},
				{
					className: 'col-8',
					key: 'transitNumber',
					type: 'input',
					props: {
						label: 'Transit Number',
						required: true,
						maxLength: 5,
						pattern: /^\d{5}$/,
						appearance: 'outline',
						attributes: { autocomplete: 'off', inputmode: 'numeric' },
					},
					validation: {
						messages: { pattern: 'Transit number must be exactly 5 digits' },
					},
				},
				{
					className: 'col-12',
					key: 'accountNumber',
					type: 'input',
					props: {
						label: 'Account Number',
						required: true,
						maxLength: 20,
						pattern: /^\d{1,20}$/,
						appearance: 'outline',
						attributes: { autocomplete: 'off', inputmode: 'numeric' },
					},
					validation: {
						messages: { pattern: 'Account number must be 1-20 digits' },
					},
				},
			],
		},
	];

	isSaving = false;
	errorMessage: string | null = null;

	private readonly original: TransactionEditModel;
	private readonly destroy$ = new Subject<void>();

	constructor(
		public readonly dialogRef: MatDialogRef<TransactionEditDialogComponent, TransactionEditDialogResult>,
		@Inject(MAT_DIALOG_DATA) public readonly data: TransactionEditDialogData,
		private readonly paymentProcessingService: PaymentProcessingService,
	) {
		const tx = data.transaction;
		this.dueDate = tx.dueDate ? dayjs(tx.dueDate).format('YYYY-MM-DD') : '—';
		this.original = {
			amount: tx.amountInCents != null ? tx.amountInCents / 100 : null,
			institutionCode: tx.institutionCode ?? '',
			transitNumber: tx.transitNumber ?? '',
			accountNumber: tx.accountNumber ?? '',
		};
		this.model = { ...this.original };
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	/** An Invalid row is re-queued by the act of saving, so saving it is never a no-op. */
	get willRequeue(): boolean {
		return this.data.transaction.status === KapchargeTransactionStatus.Invalid;
	}

	get canSave(): boolean {
		return this.form.valid && !this.isSaving && (this.willRequeue || this.buildPatch() !== null);
	}

	onCancel(): void {
		this.dialogRef.close();
	}

	onSave(): void {
		if (this.form.invalid) {
			this.form.markAllAsTouched();
			return;
		}
		if (this.isSaving) return;

		const changed = this.buildPatch();
		if (!changed && !this.willRequeue) return;

		const patch: TransactionUpdatePayload = {
			...(changed ?? {}),
			// Guard against the submit cron claiming the row while the dialog was open. The
			// row's status when it opened is the one the save is valid for, whichever it was.
			expectedStatus: this.data.transaction.status ?? undefined,
		};
		if (this.willRequeue) {
			patch.status = KapchargeTransactionStatus.Queued;
			patch.error = null;
		}

		this.isSaving = true;
		this.errorMessage = null;

		this.paymentProcessingService
			.updateTransaction(this.data.transaction.id, patch)
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: (res) => {
					this.isSaving = false;
					this.dialogRef.close(res.transaction);
				},
				error: (err) => {
					this.isSaving = false;
					this.errorMessage =
						err?.status === 409
							? 'This transaction has already been picked up for submission and can no longer be edited.'
							: err?.error?.message ?? err?.message ?? 'Failed to update the transaction.';
				},
			});
	}

	/**
	 * The changed fields only, or null when nothing was touched — a patch this endpoint
	 * ignores unknown and absent keys for, so sending only what moved keeps the write
	 * narrow and leaves concurrent edits to other columns alone.
	 */
	private buildPatch(): TransactionUpdatePayload | null {
		const patch: TransactionUpdatePayload = {};

		if (toCents(this.model.amount) !== toCents(this.original.amount)) {
			patch.amountInCents = toCents(this.model.amount);
		}
		if (this.model.institutionCode !== this.original.institutionCode) {
			patch.institutionCode = this.model.institutionCode.trim();
		}
		if (this.model.transitNumber !== this.original.transitNumber) {
			patch.transitNumber = this.model.transitNumber.trim();
		}
		if (this.model.accountNumber !== this.original.accountNumber) {
			patch.accountNumber = this.model.accountNumber.trim();
		}

		return Object.keys(patch).length > 0 ? patch : null;
	}
}